import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { useTenant } from "../contexts/TenantContext";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { CustomerSearchSelect } from "../components/CustomerSearchSelect";

// Helper to format date in Turkey timezone
const formatDateInTurkeyTZ = (dateValue, formatStr = "dd MMM yyyy") => {
  if (!dateValue) return "";
  try {
    // Parse the date and adjust for Turkey timezone
    const date = new Date(dateValue);
    // Create a date string in Turkey timezone
    const turkeyDateStr = date.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' });
    const turkeyDate = new Date(turkeyDateStr);
    return format(turkeyDate, formatStr, { locale: tr });
  } catch (e) {
    return "";
  }
};

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus, Search, ClipboardList, CalendarIcon, MapPin, 
  User, Pencil, Trash2, Eye, Building2, Download 
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Audits = () => {
  const { user } = useContext(AuthContext);
  const { tenantSlug } = useTenant();
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all"); // "all", "2", "3"
  const [auditorFilter, setAuditorFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAudit, setEditingAudit] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: "",
    title: "",
    description: "",
    audit_type: "",
    scheduled_date: null,
    end_date: null,
    location: "",
    assigned_auditor_id: "",
    status: "planned",  // Default status
    cancellation_reason: "",
    checklist_items: [],
    // Document generation fields
    certification_type: "",
    audit_mode: "",
    task_notification_date: null,
    audit_duration_days: "",
    duration_adjustment_reason: "",
    duration_adjustment_percent: "",
    final_duration_days: "",
    additional_auditors: [],
    observer_name: "",
    evaluator_name: "",
    is_facility_closed: false,  // Açık/Kapalı tesis durumu
    has_consultant: false,  // Danışman var/yok
    consultant_name: ""  // Danışman adı
  });

  // Tenant-specific audit types
  const trbAuditTypes = [
    { value: "initial", label: "İlk Denetim" },
    { value: "surveillance", label: "Gözetim Denetimi" },
    { value: "recertification", label: "Yeniden Belgelendirme" }
  ];

  const royalcertAuditTypes = [
    { value: "initial_stage2", label: "İlk Denetim - 2. Aşama (TR-I)" },
    { value: "initial_stage3", label: "İlk Denetim - 3. Aşama (GSTC)" },
    { value: "surveillance_stage2", label: "Gözetim - 2. Aşama (TR-I)" },
    { value: "surveillance_stage3", label: "Gözetim - 3. Aşama (GSTC)" },
    { value: "recert_stage2", label: "Yeniden Belgelendirme - 2. Aşama (TR-I)" },
    { value: "recert_stage3", label: "Yeniden Belgelendirme - 3. Aşama (GSTC)" }
  ];

  // Get audit types based on tenant
  const auditTypes = tenantSlug === 'royalcert' ? royalcertAuditTypes : trbAuditTypes;

  // Options for new fields - TRB specific
  const trbCertificationTypes = [
    { value: "stage1", label: "1. Aşama Belge (%30)" },
    { value: "stage2", label: "2. Aşama Belge (%70)" },
    { value: "stage3", label: "3. Aşama Belge (%100)" },
    { value: "surveillance1", label: "1. Gözetim Denetimi" },
    { value: "surveillance2", label: "2. Gözetim Denetimi" },
    { value: "recertification", label: "Yeniden Belgelendirme" }
  ];

  // RoyalCert certification types
  const royalcertCertificationTypes = [
    { value: "tr_i", label: "TR-I (2. Aşama)" },
    { value: "gstc", label: "GSTC (3. Aşama)" }
  ];

  const certificationTypes = tenantSlug === 'royalcert' ? royalcertCertificationTypes : trbCertificationTypes;

  const auditModes = [
    { value: "onsite", label: "Yerinde Denetim" },
    { value: "remote", label: "Uzaktan Denetim" },
    { value: "hybrid", label: "Hibrit (Yerinde + Uzaktan)" }
  ];

  const canManageAudits = user?.role === "admin" || user?.role === "super_admin" || user?.role === "planning_expert";

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(response.data);
    } catch (error) {
      console.error("Customers error:", error);
    }
  }, []);

  const fetchAuditors = useCallback(async () => {
    try {
      // Fetch users who can be assigned as auditors (auditor, technical_manager, admin roles)
      const [auditorsRes, techManagersRes, adminsRes] = await Promise.all([
        axios.get(`${API}/users/role/auditor`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/users/role/technical_manager`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/users/role/admin`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      // Combine all potential auditors and remove duplicates by user_id
      const allAuditors = [...auditorsRes.data, ...techManagersRes.data, ...adminsRes.data];
      const uniqueAuditors = allAuditors.filter((user, index, self) => 
        index === self.findIndex(u => u.user_id === user.user_id)
      );
      setAuditors(uniqueAuditors);
    } catch (error) {
      console.error("Auditors error:", error);
    }
  }, []);

  const handleExportAudits = async () => {
    // Build query params with date filters
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    
    try {
      const response = await axios.get(`${API}/export/audits?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateRange = startDate && endDate ? `_${startDate}_${endDate}` : "";
      link.setAttribute('download', `denetimler${dateRange}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Denetim listesi indirildi");
    } catch (error) {
      toast.error("İndirme başarısız");
    }
  };

  // Download approved report as PDF
  const handleDownloadReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/export/interactive-report/${reportId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapor_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Rapor indirildi");
    } catch (error) {
      toast.error("Rapor indirilemedi");
    }
  };

  useEffect(() => {
    fetchAudits();
    // Always fetch customers for phase filtering (all users need this)
    fetchCustomers();
    if (canManageAudits) {
      fetchAuditors();
    }
  }, [canManageAudits, fetchCustomers, fetchAuditors]);

  // Auto-fill audit_phase and manday when customer is selected (only for new audits)
  useEffect(() => {
    if (formData.customer_id && !editingAudit) {
      const selectedCustomer = customers.find(c => c.customer_id === formData.customer_id);
      
      if (selectedCustomer) {
        // Auto-fill audit_phase from customer if available
        const customerPhase = selectedCustomer.audit_phase;
        
        // Auto-fill audit_type based on customer's audit_phase if available
        let autoAuditType = formData.audit_type;
        if (customerPhase && !formData.audit_type) {
          // Set default audit type based on customer's phase
          if (customerPhase === "2" || customerPhase === 2) {
            autoAuditType = "initial_stage2";
          } else if (customerPhase === "3" || customerPhase === 3) {
            autoAuditType = "initial_stage3";
          }
        }
        
        // Only auto-fill manday for Stage 3 audits
        const isStage3 = autoAuditType?.includes('stage3') || autoAuditType?.includes('_3') || customerPhase === "3" || customerPhase === 3;
        
        setFormData(prev => ({
          ...prev,
          audit_type: autoAuditType || prev.audit_type,
          audit_duration_days: isStage3 && selectedCustomer?.manday ? selectedCustomer.manday.toString() : prev.audit_duration_days || "",
          final_duration_days: isStage3 && selectedCustomer?.manday ? selectedCustomer.manday.toString() : prev.final_duration_days || ""
        }));
      }
    }
  }, [formData.customer_id, customers, editingAudit]);

  const fetchAudits = async () => {
    try {
      const response = await axios.get(`${API}/audits`, { withCredentials: true });
      setAudits(response.data);
    } catch (error) {
      toast.error("Denetimler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Helper to format date as YYYY-MM-DD in local timezone (not UTC)
  const formatDateLocal = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.scheduled_date) {
      toast.error("Lütfen denetim tarihini seçin");
      return;
    }
    try {
      const payload = {
        ...formData,
        // Ensure status has a default value
        status: formData.status || "planned",
        // Send dates as YYYY-MM-DD strings in local timezone to avoid UTC shift
        scheduled_date: formatDateLocal(formData.scheduled_date),
        end_date: formatDateLocal(formData.end_date),
        task_notification_date: formatDateLocal(formData.task_notification_date),
        audit_duration_days: formData.audit_duration_days ? parseFloat(formData.audit_duration_days) : null,
        duration_adjustment_percent: formData.duration_adjustment_percent ? parseFloat(formData.duration_adjustment_percent) : null,
        final_duration_days: formData.final_duration_days ? parseFloat(formData.final_duration_days) : null
      };
      
      if (editingAudit) {
        await axios.put(`${API}/audits/${editingAudit.audit_id}`, payload, { withCredentials: true });
        toast.success("Denetim güncellendi");
      } else {
        await axios.post(`${API}/audits`, payload, { withCredentials: true });
        toast.success("Denetim oluşturuldu");
      }
      setDialogOpen(false);
      resetForm();
      fetchAudits();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleEdit = (audit) => {
    setEditingAudit(audit);
    setFormData({
      customer_id: audit.customer_id,
      title: audit.title,
      description: audit.description || "",
      audit_type: audit.audit_type,
      scheduled_date: new Date(audit.scheduled_date),
      end_date: audit.end_date ? new Date(audit.end_date) : null,
      location: audit.location || "",
      assigned_auditor_id: audit.assigned_auditor_id || "",
      status: audit.status || "planned",
      cancellation_reason: audit.cancellation_reason || "",
      checklist_items: audit.checklist_items || [],
      // New fields
      certification_type: audit.certification_type || "",
      risk_level: audit.risk_level || "",
      audit_mode: audit.audit_mode || "",
      task_notification_date: audit.task_notification_date ? new Date(audit.task_notification_date) : null,
      audit_duration_days: audit.audit_duration_days || "",
      duration_adjustment_reason: audit.duration_adjustment_reason || "",
      duration_adjustment_percent: audit.duration_adjustment_percent || "",
      final_duration_days: audit.final_duration_days || "",
      additional_auditors: audit.additional_auditors || [],
      observer_name: audit.observer_name || "",
      evaluator_name: audit.evaluator_name || "",
      is_facility_closed: audit.is_facility_closed || false,
      has_consultant: audit.has_consultant || false,
      consultant_name: audit.consultant_name || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (auditId) => {
    if (!window.confirm("Bu denetimi silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/audits/${auditId}`, { withCredentials: true });
      toast.success("Denetim silindi");
      fetchAudits();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      title: "",
      description: "",
      audit_type: "",
      scheduled_date: null,
      end_date: null,
      location: "",
      assigned_auditor_id: "",
      status: "planned",  // Default status
      cancellation_reason: "",
      checklist_items: [],
      certification_type: "",
      risk_level: "",
      audit_mode: "",
      task_notification_date: null,
      audit_duration_days: "",
      duration_adjustment_reason: "",
      duration_adjustment_percent: "",
      final_duration_days: "",
      additional_auditors: [],
      observer_name: "",
      evaluator_name: "",
      is_facility_closed: false,
      has_consultant: false,
      consultant_name: ""
    });
    setEditingAudit(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      planned: "bg-blue-100 text-blue-800",
      in_progress: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-gray-100 text-gray-800"
    };
    const labels = {
      planned: "Planlandı",
      in_progress: "Devam Ediyor",
      completed: "Tamamlandı",
      cancelled: "İptal"
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.planned}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredAudits = audits.filter(a => {
    const matchesSearch = a.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.auditor_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || a.status === statusFilter;
    
    // Determine audit phase from customer or audit_type
    const customer = customers.find(c => c.customer_id === a.customer_id);
    let auditPhase = customer?.audit_phase;
    
    // If customer doesn't have audit_phase, try to infer from audit_type
    if (!auditPhase && a.audit_type) {
      if (a.audit_type.includes('stage2') || a.audit_type.includes('_2')) {
        auditPhase = "2";
      } else if (a.audit_type.includes('stage3') || a.audit_type.includes('_3')) {
        auditPhase = "3";
      }
    }
    
    // Default to "3" if still not determined
    auditPhase = auditPhase || "3";
    
    const matchesPhase = phaseFilter === "all" || auditPhase === phaseFilter;
    
    // Auditor filter
    const matchesAuditor = auditorFilter === "all" || a.assigned_auditor_id === auditorFilter;
    
    // Date range filter
    let matchesDate = true;
    if (startDate) {
      const auditDate = a.scheduled_date ? a.scheduled_date.split('T')[0] : '';
      if (auditDate < startDate) matchesDate = false;
    }
    if (endDate) {
      const auditDate = a.scheduled_date ? a.scheduled_date.split('T')[0] : '';
      if (auditDate > endDate) matchesDate = false;
    }
    
    return matchesSearch && matchesStatus && matchesDate && matchesPhase && matchesAuditor;
  });

  // Helper function to get audit phase
  const getAuditPhase = (audit) => {
    const customer = customers.find(c => c.customer_id === audit.customer_id);
    let phase = customer?.audit_phase;
    
    if (!phase && audit.audit_type) {
      if (audit.audit_type.includes('stage2') || audit.audit_type.includes('_2')) {
        phase = "2";
      } else if (audit.audit_type.includes('stage3') || audit.audit_type.includes('_3')) {
        phase = "3";
      }
    }
    
    return phase || "3";
  };

  // Separate audits by phase
  const phase2Audits = filteredAudits.filter(a => getAuditPhase(a) === "2");
  const phase3Audits = filteredAudits.filter(a => getAuditPhase(a) === "3");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Denetimler</h1>
          <p className="text-[#4B5563]">Denetim planlarını yönetin</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManageAudits && (
            <Button variant="outline" onClick={handleExportAudits} data-testid="export-audits-btn">
              <Download className="w-4 h-4 mr-2" />
              Excel İndir
            </Button>
          )}
          {canManageAudits && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90" data-testid="add-audit-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Denetim
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingAudit ? "Denetim Düzenle" : "Yeni Denetim Planla"}</DialogTitle>
                  <DialogDescription>Denetim bilgilerini girin</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="customer">Müşteri *</Label>
                    <CustomerSearchSelect
                      customers={customers.filter(c => c.sales_status === 'sozlesme_imzalandi')}
                      value={formData.customer_id}
                      onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                      placeholder="Müşteri ara ve seç..."
                    />
                    {customers.filter(c => c.sales_status === 'sozlesme_imzalandi').length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠ Sözleşme imzalanmış müşteri bulunamadı. Önce Satış modülünden müşteri sözleşmesi imzalanmalıdır.
                      </p>
                    )}
                    {formData.customer_id && (() => {
                      const selectedCustomer = customers.find(c => c.customer_id === formData.customer_id);
                      if (selectedCustomer?.audit_phase) {
                        return (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Müşterinin kayıtlı aşaması: {selectedCustomer.audit_phase}. Aşama - Denetim türü otomatik seçildi
                          </p>
                        );
                      } else {
                        return (
                          <p className="text-xs text-amber-600 mt-1">
                            ⚠ Müşterinin kayıtlı aşama bilgisi yok - Lütfen denetim türünü manuel seçin
                          </p>
                        );
                      }
                    })()}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="title">Denetim Başlığı *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      data-testid="audit-title-input"
                      placeholder="Örn: 2024 Yılı İlk Denetim"
                    />
                  </div>
                  <div>
                    <Label htmlFor="audit_type">Denetim Türü *</Label>
                    <Select
                      value={formData.audit_type}
                      onValueChange={(value) => setFormData({ ...formData, audit_type: value })}
                      required
                    >
                      <SelectTrigger data-testid="audit-type-select">
                        <SelectValue placeholder="Seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {auditTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="auditor">Denetçi</Label>
                    <Select
                      value={formData.assigned_auditor_id}
                      onValueChange={(value) => setFormData({ ...formData, assigned_auditor_id: value })}
                    >
                      <SelectTrigger data-testid="audit-auditor-select">
                        <SelectValue placeholder="Denetçi seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {auditors.map((a) => (
                          <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Başlangıç Tarihi *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="audit-date-picker">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formData.scheduled_date ? format(formData.scheduled_date, "dd MMM yyyy", { locale: tr }) : "Tarih seçin"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.scheduled_date}
                          onSelect={(date) => setFormData({ ...formData, scheduled_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Bitiş Tarihi</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formData.end_date ? format(formData.end_date, "dd MMM yyyy", { locale: tr }) : "Tarih seçin"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.end_date}
                          onSelect={(date) => setFormData({ ...formData, end_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="location">Konum</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      data-testid="audit-location-input"
                      placeholder="Denetim yapılacak adres"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="description">Açıklama</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      data-testid="audit-description-input"
                      placeholder="Denetim hakkında notlar..."
                      rows={3}
                    />
                  </div>

                  {/* Divider for new fields */}
                  <div className="md:col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium text-[#1A4D2E] mb-3">Denetim Bilgileri</h4>
                  </div>

                  <div>
                    <Label>Belgelendirme Türü</Label>
                    <Select
                      value={formData.certification_type}
                      onValueChange={(value) => setFormData({ ...formData, certification_type: value })}
                    >
                      <SelectTrigger data-testid="certification-type-select">
                        <SelectValue placeholder="Seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {certificationTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Denetim Modu</Label>
                    <Select
                      value={formData.audit_mode}
                      onValueChange={(value) => setFormData({ ...formData, audit_mode: value })}
                    >
                      <SelectTrigger data-testid="audit-mode-select">
                        <SelectValue placeholder="Seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {auditModes.map((mode) => (
                          <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Tesis Durumu</Label>
                    <Select
                      value={formData.is_facility_closed ? "closed" : "open"}
                      onValueChange={(value) => setFormData({ ...formData, is_facility_closed: value === "closed" })}
                    >
                      <SelectTrigger data-testid="facility-status-select">
                        <SelectValue placeholder="Seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            Açık Tesis
                          </span>
                        </SelectItem>
                        <SelectItem value="closed">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                            Kapalı Tesis
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.is_facility_closed 
                        ? "Kapalı tesis sertifikası 3 ay geçerli olur" 
                        : "Açık tesis sertifikası 1 yıl geçerli olur"}
                    </p>
                  </div>

                  <div>
                    <Label>Görev Bildirim Tarihi</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formData.task_notification_date ? format(formData.task_notification_date, "dd MMM yyyy", { locale: tr }) : "Tarih seçin"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.task_notification_date}
                          onSelect={(date) => setFormData({ ...formData, task_notification_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Divider for duration fields */}
                  <div className="md:col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium text-[#1A4D2E] mb-3">Denetim Süresi</h4>
                    {/* Show info based on audit type */}
                    {formData.audit_type?.includes('stage2') && (
                      <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded mb-3">
                        ℹ️ 2. Aşama denetimleri için adam gün değerini manuel olarak giriniz.
                      </p>
                    )}
                    {formData.audit_type?.includes('stage3') && !editingAudit && (
                      <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded mb-3">
                        ℹ️ 3. Aşama için adam gün değeri müşterinin kayıtlı hesabından otomatik alınır.
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>Tespit Edilen Süre (Adam/Gün)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.audit_duration_days}
                      readOnly={!editingAudit && formData.audit_type?.includes('stage3')}
                      className={(!editingAudit && formData.audit_type?.includes('stage3')) ? "bg-gray-100 cursor-not-allowed" : ""}
                      onChange={(e) => {
                        // Allow editing for Stage 2 or when in edit mode
                        if (editingAudit || formData.audit_type?.includes('stage2')) {
                          setFormData({ ...formData, audit_duration_days: e.target.value });
                        }
                      }}
                      placeholder={formData.audit_type?.includes('stage2') ? "Manuel giriş yapın (Örn: 1.5)" : "Adam-Gün Hesaplama'dan gelir"}
                    />
                    {!editingAudit && formData.audit_type?.includes('stage3') && (
                      <p className="text-xs text-gray-500 mt-1">
                        Bu değer Adam-Gün Hesaplama modülünden otomatik gelir
                      </p>
                    )}
                  </div>

                  <div>
                    <Label>İndirim/Artırım Yüzdesi</Label>
                    <Input
                      type="number"
                      value={formData.duration_adjustment_percent}
                      onChange={(e) => setFormData({ ...formData, duration_adjustment_percent: e.target.value })}
                      placeholder="Örn: -10 veya +15"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label>İndirim/Artırım Gerekçesi</Label>
                    <Input
                      value={formData.duration_adjustment_reason}
                      onChange={(e) => setFormData({ ...formData, duration_adjustment_reason: e.target.value })}
                      placeholder="Gerekçe..."
                    />
                  </div>

                  <div>
                    <Label>Nihai Süre (Adam/Gün)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.final_duration_days}
                      onChange={(e) => setFormData({ ...formData, final_duration_days: e.target.value })}
                      placeholder="Örn: 1.5"
                    />
                  </div>

                  {/* Divider for team fields */}
                  <div className="md:col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium text-[#1A4D2E] mb-3">Denetim Ekibi</h4>
                  </div>

                  <div>
                    <Label>Gözlemci</Label>
                    <Input
                      value={formData.observer_name}
                      onChange={(e) => setFormData({ ...formData, observer_name: e.target.value })}
                      placeholder="Gözlemci adı"
                    />
                  </div>

                  <div>
                    <Label>Değerlendirici</Label>
                    <Input
                      value={formData.evaluator_name}
                      onChange={(e) => setFormData({ ...formData, evaluator_name: e.target.value })}
                      placeholder="Değerlendirici adı"
                    />
                  </div>

                  {/* Consultant fields */}
                  <div className="md:col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium text-[#1A4D2E] mb-3">Danışman Bilgisi</h4>
                  </div>

                  <div>
                    <Label>Danışman</Label>
                    <Select
                      value={formData.has_consultant ? "yes" : "no"}
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        has_consultant: value === "yes",
                        consultant_name: value === "no" ? "" : formData.consultant_name
                      })}
                    >
                      <SelectTrigger data-testid="consultant-select">
                        <SelectValue placeholder="Seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">Danışman Yok</SelectItem>
                        <SelectItem value="yes">Danışman Var</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.has_consultant && (
                    <div>
                      <Label>Danışman Adı</Label>
                      <Input
                        value={formData.consultant_name}
                        onChange={(e) => setFormData({ ...formData, consultant_name: e.target.value })}
                        placeholder="Danışman adı/firması"
                        data-testid="consultant-name-input"
                      />
                    </div>
                  )}

                  {/* Status Section - Only show when editing */}
                  {editingAudit && (
                    <>
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <h4 className="font-medium text-[#1A4D2E] mb-3">Denetim Durumu</h4>
                      </div>

                      <div>
                        <Label>Durum</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger data-testid="status-select">
                            <SelectValue placeholder="Durum seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="planned">Planlandı</SelectItem>
                            <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                            <SelectItem value="completed">Tamamlandı</SelectItem>
                            <SelectItem value="cancelled">İptal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.status === "cancelled" && (
                        <div className="md:col-span-2">
                          <Label className="text-red-600">İptal Gerekçesi *</Label>
                          <Textarea
                            value={formData.cancellation_reason}
                            onChange={(e) => setFormData({ ...formData, cancellation_reason: e.target.value })}
                            placeholder="İptal gerekçesini yazın..."
                            className="border-red-300 focus:border-red-500"
                            rows={3}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    İptal
                  </Button>
                  <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90" data-testid="save-audit-btn">
                    {editingAudit ? "Güncelle" : "Kaydet"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-[#E8F3EE]">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder="Denetim ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="audit-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="audit-status-filter">
                <SelectValue placeholder="Durum filtrele" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="planned">Planlandı</SelectItem>
                <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                <SelectItem value="completed">Tamamlandı</SelectItem>
                <SelectItem value="cancelled">İptal</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Phase Filter */}
            {tenantSlug === 'royalcert' && (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-500">Aşama:</span>
                <Button
                  variant={phaseFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPhaseFilter("all")}
                  className={phaseFilter === "all" ? "bg-[#1A4D2E]" : ""}
                >
                  Tümü
                </Button>
                <Button
                  variant={phaseFilter === "2" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPhaseFilter("2")}
                  className={phaseFilter === "2" ? "bg-orange-600" : "border-orange-300 text-orange-600"}
                >
                  2. Aşama
                </Button>
                <Button
                  variant={phaseFilter === "3" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPhaseFilter("3")}
                  className={phaseFilter === "3" ? "bg-blue-600" : "border-blue-300 text-blue-600"}
                >
                  3. Aşama
                </Button>
              </div>
            )}
            
            {/* Auditor Filter */}
            <Select value={auditorFilter} onValueChange={setAuditorFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Denetçi filtrele" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Denetçiler</SelectItem>
                {auditors.map(a => (
                  <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500 whitespace-nowrap">Tarih:</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36"
                placeholder="Başlangıç"
              />
              <span className="text-gray-400">-</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36"
                placeholder="Bitiş"
              />
              {(startDate || endDate || phaseFilter !== "all" || auditorFilter !== "all") && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setStartDate(""); setEndDate(""); setPhaseFilter("all"); setAuditorFilter("all"); }}
                  className="text-gray-500 px-2"
                >
                  Filtreleri Temizle
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audits List */}
      <Card className="border-[#E8F3EE]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : filteredAudits.length === 0 ? (
            <div className="p-8 text-center text-[#4B5563]">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{searchTerm ? "Sonuç bulunamadı" : "Henüz denetim planlanmamış"}</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead>Denetim</TableHead>
                      <TableHead>Müşteri</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Denetçi</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAudits.map((audit) => (
                      <TableRow key={audit.audit_id} className="hover:bg-[#E8F3EE]/30">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center">
                              <ClipboardList className="w-5 h-5 text-[#1A4D2E]" />
                            </div>
                            <div>
                              <p className="font-medium text-[#111827]">{audit.title}</p>
                              <p className="text-xs text-[#9CA3AF]">
                                {auditTypes.find(t => t.value === audit.audit_type)?.label || audit.audit_type}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#9CA3AF]" />
                            <span>{audit.customer_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <CalendarIcon className="w-4 h-4 text-[#9CA3AF]" />
                            {formatDateInTurkeyTZ(audit.scheduled_date)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {audit.assigned_auditor_name ? (
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-[#9CA3AF]" />
                              <span>{audit.assigned_auditor_name}</span>
                            </div>
                          ) : (
                            <span className="text-[#9CA3AF]">-</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(audit.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Download approved report button */}
                            {audit.has_approved_report && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownloadReport(audit.approved_report_id)}
                                title="Onaylı Raporu İndir"
                                data-testid={`download-report-${audit.audit_id}`}
                              >
                                <Download className="w-4 h-4 text-[#1A4D2E]" />
                              </Button>
                            )}
                            {/* View button - all roles can view audit details */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/audits/${audit.audit_id}`)}
                              data-testid={`view-audit-${audit.audit_id}`}
                            >
                              <Eye className="w-4 h-4 text-[#4B5563]" />
                            </Button>
                            {canManageAudits && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(audit)}
                                  data-testid={`edit-audit-${audit.audit_id}`}
                                >
                                  <Pencil className="w-4 h-4 text-[#4B5563]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(audit.audit_id)}
                                  data-testid={`delete-audit-${audit.audit_id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-[#E8F3EE]">
                {filteredAudits.map((audit) => (
                  <div key={audit.audit_id} className="p-4 hover:bg-[#E8F3EE]/30">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center flex-shrink-0">
                          <ClipboardList className="w-5 h-5 text-[#1A4D2E]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-[#111827] truncate">{audit.title}</p>
                          <p className="text-xs text-[#9CA3AF]">
                            {auditTypes.find(t => t.value === audit.audit_type)?.label || audit.audit_type}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(audit.status)}
                      </div>
                    </div>
                    <div className="mt-3 space-y-2 text-sm text-[#4B5563]">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />
                        <span className="truncate">{audit.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />
                        {format(new Date(audit.scheduled_date), "dd MMM yyyy", { locale: tr })}
                      </div>
                      {audit.assigned_auditor_name && (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />
                          <span>{audit.assigned_auditor_name}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-2 pt-2 border-t border-[#E8F3EE]">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/audits/${audit.audit_id}`)}
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Görüntüle
                      </Button>
                      {canManageAudits && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(audit)}
                            className="h-8 w-8"
                          >
                            <Pencil className="w-4 h-4 text-[#4B5563]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(audit.audit_id)}
                            className="h-8 w-8"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Audits;
